import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Brain, BarChart3, Shield, Zap, TrendingUp, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { staticInsights } from '@/data/staticInsights';

const HERO_BG = "/images/ai-consulting-hero.webp";

const industries = [
  {
    name: 'Retail',
    image: '/images/retail-ai.webp',
    desc: 'AI-powered demand forecasting, personalization, and supply chain optimization.'
  },
  {
    name: 'Manufacturing',
    image: '/images/manufacturing-ai.webp',
    desc: 'Predictive maintenance, quality control, and smart factory solutions.'
  },
  {
    name: 'Energy & Utilities',
    image: '/images/energy-utilities-ai.webp',
    desc: 'Smart grid optimization, renewable integration, and energy analytics.'
  },
  {
    name: 'Telecom',
    image: '/images/telecom-ai.webp',
    desc: 'Network optimization, churn reduction, and 5G intelligence.'
  }
];

const capabilities = [
  { icon: Brain, title: 'AI Strategy & Roadmap', desc: 'Develop comprehensive AI strategies aligned with your business objectives and market position.', accent: 'teal' },
  { icon: BarChart3, title: 'Data & Analytics', desc: 'Transform raw data into actionable insights with advanced analytics and ML pipelines.', accent: 'orange' },
  { icon: Shield, title: 'Responsible AI', desc: 'Build ethical, transparent, and compliant AI systems that earn stakeholder trust.', accent: 'teal' },
  { icon: Zap, title: 'AI Implementation', desc: 'End-to-end deployment of production-grade AI solutions that deliver measurable ROI.', accent: 'orange' },
  { icon: TrendingUp, title: 'Digital Transformation', desc: 'Modernize legacy systems and processes with intelligent automation and AI integration.', accent: 'teal' },
  { icon: Users, title: 'Change Management', desc: 'Drive organizational adoption of AI with tailored training and change programs.', accent: 'orange' },
];

const valuePrinciples = [
  { icon: Brain, title: 'Use-Case Clarity', desc: 'Identify the AI opportunities that are practical, valuable, and aligned to real business priorities.' },
  { icon: Zap, title: 'Solution Build', desc: 'Design and implement focused AI and analytics solutions that fit existing operations.' },
  { icon: Shield, title: 'Responsible Deployment', desc: 'Put governance, explainability, and privacy-aware practices into the delivery process.' },
  { icon: Users, title: 'Operational Adoption', desc: 'Help teams adopt new tools, workflows, and decisions with confidence.' },
];

export default function HomePage() {
  const insights = staticInsights.slice(0, 3);

  return (
    <div>
      {/* Hero Section */}
      <section data-testid="hero-section" className="relative min-h-[90vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-white via-white/95 to-white/70" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <div className="max-w-3xl">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-teal mb-6 opacity-0 animate-fade-in-up stagger-1">
              AI Consulting for Enterprise
            </p>
            <h1
              data-testid="hero-tagline"
              className="font-heading text-3xl sm:text-5xl lg:text-6xl font-medium text-navy leading-[1.12] mb-6 opacity-0 animate-fade-in-up stagger-2"
            >
              Effective AI Consulting
              <span className="block text-teal">for Measurable Business Outcomes.</span>
            </h1>
            <p className="text-base md:text-lg text-navy/60 leading-relaxed mb-10 max-w-xl opacity-0 animate-fade-in-up stagger-3">
              We help businesses identify high-value AI use cases, build practical solutions, and deploy responsible AI systems that make operations smoother, faster, and more reliable.
            </p>
            <div className="flex flex-wrap gap-4 opacity-0 animate-fade-in-up stagger-4">
              <Link to="/contact">
                <Button data-testid="hero-cta" className="bg-navy text-white hover:bg-navy/90 rounded-full px-8 py-3 text-base h-auto">
                  Schedule a Consultation <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <Link to="/services">
                <Button data-testid="hero-cta-secondary" variant="outline" className="border-navy/20 text-navy hover:bg-navy/5 rounded-full px-8 py-3 text-base h-auto">
                  Explore Services
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Capabilities Section */}
      <section data-testid="capabilities-section" className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="mb-16">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-teal mb-4">Our Capabilities</p>
            <h2 className="font-heading text-3xl md:text-4xl font-medium text-navy">
              End-to-End AI Excellence
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {capabilities.map((cap, i) => {
              const Icon = cap.icon;
              return (
                <div
                  key={cap.title}
                  data-testid={`capability-card-${i}`}
                  className="group p-8 md:p-12 bg-secondary border border-border rounded-sm hover:-translate-y-1 hover:shadow-lg transition-all duration-300"
                >
                  <div className={`w-12 h-12 rounded-sm flex items-center justify-center mb-6 ${
                    cap.accent === 'teal' ? 'bg-teal/10 text-teal' : 'bg-accent-orange/10 text-accent-orange'
                  }`}>
                    <Icon className="h-6 w-6" />
                  </div>
                  <h3 className="font-heading text-xl font-medium text-navy mb-3">{cap.title}</h3>
                  <p className="text-sm text-navy/60 leading-relaxed">{cap.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Industries Section */}
      <section data-testid="industries-section" className="py-24 md:py-32 bg-secondary">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="mb-16">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-accent-orange mb-4">Industries We Serve</p>
            <h2 className="font-heading text-3xl md:text-4xl font-medium text-navy">
              Deep Domain Expertise
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {industries.map((ind, i) => (
              <Link
                key={ind.name}
                to="/services"
                data-testid={`industry-card-${i}`}
                className="group relative aspect-[3/4] rounded-sm overflow-hidden"
              >
                <img src={ind.image} alt={ind.name} className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-navy/90 via-navy/40 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <h3 className="font-heading text-xl font-medium text-white mb-2">{ind.name}</h3>
                  <p className="text-white/70 text-sm leading-relaxed opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    {ind.desc}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Value Principles Section */}
      <section data-testid="value-principles-section" className="py-24 md:py-32 bg-navy text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="mb-14 max-w-3xl">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-teal mb-4">How We Create Value</p>
            <h2 className="font-heading text-3xl md:text-4xl font-medium mb-4">
              Focused AI Consulting for Real Business Problems
            </h2>
            <p className="text-white/55 leading-relaxed">
              Instead of broad claims, we focus on a simple delivery path: understand the operation, find the right AI opportunities, build responsibly, and support adoption.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {valuePrinciples.map((item, i) => {
              const Icon = item.icon;
              return (
                <div key={item.title} data-testid={`value-principle-${i}`} className="border border-white/10 bg-white/5 p-6 rounded-sm">
                  <div className="w-11 h-11 rounded-sm bg-teal/15 flex items-center justify-center mb-5">
                    <Icon className="h-5 w-5 text-teal" />
                  </div>
                  <h3 className="font-heading text-lg font-medium mb-2">{item.title}</h3>
                  <p className="text-sm text-white/55 leading-relaxed">{item.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Latest Insights */}
      {insights.length > 0 && (
        <section data-testid="insights-preview" className="py-24 md:py-32 bg-white">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="flex items-end justify-between mb-16">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] font-bold text-teal mb-4">Latest Insights</p>
                <h2 className="font-heading text-3xl md:text-4xl font-medium text-navy">
                  Thought Leadership
                </h2>
              </div>
              <Link to="/insights" className="hidden md:flex items-center text-sm text-teal hover:text-teal/80 font-medium">
                View All <ArrowRight className="ml-1 h-4 w-4" />
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
              {insights.map((post, i) => (
                <Link
                  key={post.slug}
                  to={`/insights/${post.slug}`}
                  data-testid={`insight-preview-${i}`}
                  className="group"
                >
                  <div className="aspect-[16/10] rounded-sm overflow-hidden mb-4">
                    <img
                      src={post.image_url}
                      alt={post.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-xs uppercase tracking-wider font-bold text-teal">{post.category}</span>
                    {post.industry && <span className="text-xs text-navy/40">|</span>}
                    {post.industry && <span className="text-xs text-navy/40">{post.industry}</span>}
                  </div>
                  <h3 className="font-heading text-lg font-medium text-navy group-hover:text-teal transition-colors mb-2">
                    {post.title}
                  </h3>
                  <p className="text-sm text-navy/50 leading-relaxed line-clamp-2">{post.excerpt}</p>
                </Link>
              ))}
            </div>
            <Link to="/insights" className="md:hidden flex items-center justify-center text-sm text-teal font-medium mt-8">
              View All Insights <ArrowRight className="ml-1 h-4 w-4" />
            </Link>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section data-testid="cta-section" className="py-24 md:py-32 bg-secondary">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-medium text-navy mb-6">
            Ready to Accelerate Your AI Journey?
          </h2>
          <p className="text-navy/60 text-base md:text-lg max-w-2xl mx-auto mb-10">
            Start with a focused conversation about where AI can reduce friction, improve decisions, and create measurable operational value in your business.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact">
              <Button data-testid="cta-primary" className="bg-navy text-white hover:bg-navy/90 rounded-full px-8 py-3 text-base h-auto">
                Start a Conversation <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <Link to="/about">
              <Button variant="outline" className="border-navy/20 text-navy hover:bg-navy/5 rounded-full px-8 py-3 text-base h-auto">
                Learn About Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
